import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Box, Loader, Text, Center } from '@mantine/core';
import { Alert } from '@xplatform/design-system';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import AutoSizer from 'react-virtualized-auto-sizer';
import { List } from 'react-window';
import type { Uploaded } from '@utils/extractionUtils';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
).toString();

interface MeasuredProps {
    onResize: (h: number) => void;
    children: (ref: React.RefCallback<HTMLDivElement>) => React.ReactNode;
}

const Measured: React.FC<MeasuredProps> = ({ onResize, children }) => {
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!ref.current) return;
        const el = ref.current;
        const ro = new ResizeObserver((entries) => {
            for (const e of entries) {
                const h = Math.ceil(e.contentRect.height);
                onResize(h);
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [onResize]);

    return <>{children((node) => { ref.current = node; })}</>;
};

interface PdfVirtualViewerProps {
    file: Uploaded;
    pages: number[];
    onLoadSuccess: ({ numPages }: { numPages: number }) => void;
}

const PdfVirtualViewer = React.memo(function PdfVirtualViewer({
    file,
    pages,
    onLoadSuccess
}: PdfVirtualViewerProps) {
    const listRef = useRef<any>(null);
    const sizeMap = useRef<Map<number, number>>(new Map());
    const [error, setError] = useState<string | null>(null);

    const getEstimatedItemSize = useCallback((containerWidth: number) => Math.ceil(containerWidth * 1.5), []);
    const getSize = useCallback((index: number, estimated: number) => sizeMap.current.get(index) ?? estimated, []);
    const setSize = useCallback((index: number, size: number) => {
        const prev = sizeMap.current.get(index);
        if (prev !== size) {
            sizeMap.current.set(index, size);
            listRef.current?.resetAfterIndex?.(index);
        }
    }, []);

    useEffect(() => {
        sizeMap.current.clear();
        listRef.current?.resetAfterIndex?.(0, true);
        setError(null);
    }, [pages, file]);

    const handleLoadError = useCallback((error: Error) => {
        console.error('PDF load error:', error);
        let errorMessage = 'Failed to load PDF document.';

        if (error.message.includes('CORS')) {
            errorMessage = 'Failed to load PDF: Cross-origin request blocked. Please check file permissions.';
        } else if (error.message.includes('Invalid PDF')) {
            errorMessage = 'The file appears to be corrupted or is not a valid PDF.';
        } else if (error.message.includes('password')) {
            errorMessage = 'This PDF is password-protected and cannot be displayed.';
        } else if (error.message) {
            errorMessage = `Failed to load PDF: ${error.message}`;
        }

        setError(errorMessage);
    }, []);

    const Row = useCallback(({ index, style, displayPages, containerWidth }: any) => {
        const pageNo = displayPages[index];
        return (
            <div style={{ ...style }}>
                <Measured onResize={(h) => setSize(index, h + 16)}>
                    {(measureRef) => (
                        <div ref={measureRef}>
                            <div style={{ marginBottom: '0.5rem' }}>
                                <Page
                                    pageNumber={pageNo}
                                    width={Math.max(0, containerWidth - 16)}
                                    loading={<Box p="sm" ta="center">Loading page {pageNo}…</Box>}
                                />
                            </div>
                        </div>
                    )}
                </Measured>
            </div>
        );
    }, [setSize]);

    if (error) {
        return (
            <div className="pdf-virtual-root" >
                <Center h="100%" p="md">
                    <Alert color="red" maw={500}>
                        <Text size="sm" fw={600} mb="xs">
                            PDF Loading Error
                        </Text>
                        <Text size="sm">
                            {error}
                        </Text>
                    </Alert>
                </Center>
            </div>
        );
    }

    return (
        <div className="pdf-virtual-root" >
            <AutoSizer>
                {({ height, width }) => (
                    <Document
                        file={file}
                        onLoadSuccess={onLoadSuccess}
                        onLoadError={handleLoadError}
                        loading={
                            <Center h="100%">
                                <Loader />
                            </Center>
                        }
                        error={
                            <Center h="100%" p="md">
                                <Alert color="red" maw={500}>
                                    <Text size="sm">
                                        Failed to load PDF. Please try again or select a different file.
                                    </Text>
                                </Alert>
                            </Center>
                        }
                    >
                        <List
                            style={{ height, width }}
                            rowComponent={Row}
                            rowCount={pages.length}
                            rowHeight={(index: number, rowProps: any) =>
                                getSize(index, getEstimatedItemSize(rowProps.containerWidth))
                            }
                            rowProps={{ displayPages: pages, containerWidth: width }}
                            overscanCount={2}
                            listRef={listRef}
                            defaultHeight={height}
                        />
                    </Document>
                )}
            </AutoSizer>
        </div>
    );
}, (prev, next) => {
    if (prev.file !== next.file) return false;
    if (prev.onLoadSuccess !== next.onLoadSuccess) return false;
    if (prev.pages.length !== next.pages.length) return false;
    for (let i = 0; i < prev.pages.length; i++) {
        if (prev.pages[i] !== next.pages[i]) return false;
    }
    return true;
});

export default PdfVirtualViewer;
